import { isArray, isEqual, isObject } from 'lodash';

type GenericObject = Record<string, any>;

function compareArrays<T extends GenericObject>(
  original: T[],
  modified: T[],
): Partial<T>[] {
  const result: Partial<T>[] = [];

  for (let i = 0; i < modified.length; i++) {
    const originalItem = original[i];
    const modifiedItem = modified[i];

    if (isObject(originalItem) && isObject(modifiedItem)) {
      const changedFields = getChangedFields(originalItem, modifiedItem);
      if (Object.keys(changedFields).length > 0) {
        result.push(changedFields);
      }
    } else if (!isEqual(originalItem, modifiedItem)) {
      result.push(modifiedItem);
    }
  }

  return result;
}

type Options<T> = Partial<{
  ignoreFields: Extract<keyof T, string>[];
}>;

function getChangedFields<T extends GenericObject>(
  original: T,
  modified: T,
  options: Options<T> = {},
): Partial<T> {
  if (!isObject(original) || !isObject(modified)) {
    return modified;
  }

  const result: Partial<T> = {};

  for (const key in modified) {
    const originalValue = original[key];
    const modifiedValue = modified[key];

    const isArrayValue = isArray(originalValue) && isArray(modifiedValue);

    const notEqualValue =
      !isEqual(originalValue, modifiedValue) && !isArrayValue;

    const isObjectValue =
      isObject(originalValue) && isObject(modifiedValue) && !isArrayValue;

    if (!(key in modified) || options.ignoreFields?.includes(key)) {
      continue;
    }

    if (isArrayValue) {
      if (originalValue.length && modifiedValue.length === 0) {
        result[key] = modifiedValue;
        continue;
      }

      if (originalValue.length > 0 && isObject(originalValue[0])) {
        const changedArray = compareArrays(originalValue, modifiedValue);
        if (changedArray.length > 0) {
          result[key] = changedArray as T[Extract<keyof T, string>];
        }
      } else {
        if (!isEqual(originalValue, modifiedValue)) {
          result[key] = modifiedValue;
        }
      }
      continue;
    }

    if (isObjectValue) {
      const changedFields = getChangedFields(
        originalValue,
        modifiedValue,
      ) as T[Extract<keyof T, string>];

      if (Object.keys(changedFields).length > 0) {
        result[key] = changedFields;
      }
      continue;
    }

    if (notEqualValue) {
      result[key] = modifiedValue;
      continue;
    }
  }

  return result;
}

export function buildChangedObject<T extends GenericObject>(
  original: T,
  modified: Partial<T>,
  options: Options<T> = {},
): Partial<T> {
  return getChangedFields(original, modified as T, options);
}
